import React, { useEffect, useState } from "react";
import { FormControlLabel, Switch } from "@mui/material";
import { FaqForm, FaqInterface } from "../../interfaces/faq.interface";
import { post, put } from "../../redux/ApiHelper";
import { successToast, errorToast } from "../../utils/toast.util";

interface FaqModalProps {
  isOpen: boolean;
  closeModal: () => void;
  isEdit?: boolean;
  editData?: FaqInterface | null;
  onSuccess?: () => void;
}

const defaultForm: FaqForm = {
  forWhom: "Parent",
  question: "",
  answer: "",
  order: 0,
  isActive: true,
};

const FaqModal: React.FC<FaqModalProps> = ({
  isOpen,
  closeModal,
  isEdit = false,
  editData = null,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<FaqForm>(defaultForm);
  const [errors, setErrors] = useState({ question: "", answer: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isEdit && editData) {
      setFormData({
        forWhom: editData.forWhom,
        question: editData.question || "",
        answer: editData.answer || "",
        order: editData.order ?? 0,
        isActive: editData.isActive ?? true,
      });
    } else {
      setFormData(defaultForm);
    }
    setErrors({ question: "", answer: "" });
  }, [isEdit, editData, isOpen]);

  const validateForm = (): boolean => {
    const newErrors = { question: "", answer: "" };
    let isValid = true;

    if (!formData.question.trim()) {
      newErrors.question = "Question is required";
      isValid = false;
    }
    if (!formData.answer.trim()) {
      newErrors.answer = "Answer is required";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "order" ? Number(value) || 0 : value,
    }));
    if (name === "question" || name === "answer") {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setIsSubmitting(true);
      if (isEdit && editData?._id) {
        await put("/admin/faq/update", { _id: editData._id, ...formData });
        successToast("FAQ updated successfully");
      } else {
        await post("/admin/faq/create", formData);
        successToast("FAQ created successfully");
      }
      onSuccess?.();
    } catch (error: unknown) {
      const errorMessage =
        error &&
        typeof error === "object" &&
        "message" in error &&
        typeof (error as { message?: string }).message === "string"
          ? (error as { message: string }).message
          : "Failed to save FAQ. Please try again.";
      errorToast(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-gray-800 w-full max-w-lg mx-4 p-6 rounded-lg shadow-lg max-h-screen overflow-auto border border-gray-700">
        <h2 className="text-xl font-semibold text-yellow-500 mb-4">
          {isEdit ? "Update" : "Create New"} FAQ
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-gray-300 mb-2 text-left">
              For Whom <span className="text-red-500">*</span>
            </label>
            <select
              name="forWhom"
              value={formData.forWhom}
              onChange={handleChange}
              className="w-full px-4 py-2 rounded bg-gray-700 text-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
            >
              <option value="Parent">Parent</option>
              <option value="Teacher">Teacher</option>
            </select>
          </div>

          <div>
            <label className="block text-gray-300 mb-2 text-left">
              Question <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="question"
              value={formData.question}
              onChange={handleChange}
              className={`w-full px-4 py-2 rounded bg-gray-700 text-yellow-500 focus:outline-none focus:ring-2 ${
                errors.question
                  ? "focus:ring-red-500 border-red-500"
                  : "focus:ring-yellow-500"
              }`}
              placeholder="Enter question"
            />
            {errors.question && (
              <p className="text-red-500 text-sm mt-1 text-left">
                {errors.question}
              </p>
            )}
          </div>

          <div>
            <label className="block text-gray-300 mb-2 text-left">
              Answer <span className="text-red-500">*</span>
            </label>
            <textarea
              name="answer"
              value={formData.answer}
              onChange={handleChange}
              rows={5}
              className={`w-full px-4 py-2 rounded bg-gray-700 text-yellow-500 focus:outline-none focus:ring-2 resize-y ${
                errors.answer
                  ? "focus:ring-red-500 border-red-500"
                  : "focus:ring-yellow-500"
              }`}
              placeholder="Enter answer"
            />
            {errors.answer && (
              <p className="text-red-500 text-sm mt-1 text-left">
                {errors.answer}
              </p>
            )}
          </div>

          <div>
            <label className="block text-gray-300 mb-2 text-left">Order</label>
            <input
              type="number"
              name="order"
              value={formData.order}
              onChange={handleChange}
              className="w-full px-4 py-2 rounded bg-gray-700 text-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              placeholder="Display order (lower first)"
            />
          </div>

          <div>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(_, checked) =>
                    setFormData((prev) => ({ ...prev, isActive: checked }))
                  }
                  color="warning"
                />
              }
              label={<span className="text-gray-300">Active</span>}
            />
          </div>

          <div className="mt-6 flex justify-end space-x-3">
            <button
              type="button"
              onClick={closeModal}
              className="px-4 py-2 bg-gray-600 text-gray-200 rounded-md hover:bg-gray-500 transition"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`px-4 py-2 rounded-md transition ${
                isSubmitting
                  ? "bg-yellow-600 opacity-50 cursor-not-allowed"
                  : "bg-yellow-500 text-gray-900 hover:bg-yellow-400"
              }`}
              disabled={isSubmitting}
            >
              {isSubmitting
                ? isEdit
                  ? "Updating..."
                  : "Creating..."
                : isEdit
                  ? "Update FAQ"
                  : "Create FAQ"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FaqModal;
